const ERROR_MESSAGES = {
  500: 'Oh no! We\'re currently experiencing issues on our end. Please try again.',
  520: 'An unexpected error occurred, please try again.',
  404: 'Whoops! The file you\'re looking for can\'t be found!',
  400: 'We were unable to process that request. Please try again.',
};

const handlingErrors = (statusCode, customMessage) => (
  ERROR_MESSAGES[statusCode] || customMessage || 'Oops, we ran into an issue. Please try again.'
);

const parseFailedUserDetailsPayload = (payload) => {
  const requestData = payload?.config?.data;
  if (!requestData) return {};

  if (typeof requestData === 'string') {
    try {
      return JSON.parse(requestData);
    } catch {
      return {};
    }
  }

  return requestData;
};

const getTenantId = (availableEmrs = []) => (
  availableEmrs.find(emr => emr?.tenant_id)?.tenant_id
);

const initialState = {
  // Hydration of GET /api/v1/users, as three explicit facts rather than one
  // overloaded boolean.
  //
  // `userInfoLoaded` means SETTLED — the request is no longer outstanding — so a
  // consumer can tell "this profile is empty" from "this profile has not been
  // fetched yet". Deriving that from a stored email instead is what let the
  // tutorial auto-open evaluate before `has_completed_tutorial` was known.
  //
  // Settled is not the same as correct, though: a failed fetch also settles, and
  // leaves every field at its default. A consumer about to spend a ONE-SHOT on
  // the value it read (the tutorial auto-open latch) has to require
  // `userInfoLoaded && !userInfoLoadFailed`, or a transient 500 hands it
  // `has_completed_tutorial: false` for a user the server has marked done.
  userInfoLoaded: false,
  userInfoLoading: false,
  userInfoLoadFailed: false,
  // The durable "don't offer the guided tutorials again" flag, hydrated from
  // GET /api/v1/users. Declared explicitly so a reset returns it to a real `false`
  // rather than `undefined`, and so it is discoverable in this shape at all.
  has_completed_tutorial: false,
  name: '',
  email: '',
  phone_number: '',
  job_title: '',
  organization: '',
  emr: '',
  department: '',
  departments: [],
  caller_id: '',
  associated_fax_numbers: [],
  default_fax_numbers: [],
  isAdmin: false,
  loginSuccess: false,
  loginErrorMessage: '',
  notificationErrorMessage: '',
  notification_fax_email: false,
  email_notification_emailed_records: false,
  email_notification_assigned_records: false,
  email_notification_emr_upload_failures: false,
  modalOpen: false,
  allowed_user_domains: [],
  allOrganizationDepartments: [],
  allDocumentCategories: {},
  taskTemplateCategoryUsage: [],
  apiPatients: {},
  allActiveFeatures: [],
  billingV2Routed: false,
  allReferredStatuses: [],
  referralStatusBarShowUnpinned: true,
  referralStatusBarPreferencesSaving: false,
  allDocumentStatuses: {},
  allReferralStatuses: {},
  allEmaTaskTemplates: {},
  usersDepartments: {},
  allEmaTaskProviders: [],
  allKareoTaskProviders: [],
  allMarketingRepresentatives: [],
  allPayerNames: [],
  payerPlanNamesMap: {},
  defaultSmsTemplate: null,
  defaultOutboundCallTemplate: null,
  error: false,
  errorMessage: '',
  apiError: false,
  apiErrorMessage: '',
  failedSentFaxesNotification: false,
  is_clinical_provider: null,
  npi: null,
  walkthrough_start_destination: null,
  autoArchiveSettings: {},
  emrAutoUploadCategorySettings: {},
  emaResultsProviderSettings: {},
  modmedQueuedFaxSettings: {},
  armKillSwitchSettings: {},
  emrTemplates: [],
  emrFacilities: [],
  emrTemplatesLoaded: false,
  emrFacilitiesLoaded: false,
  emrTemplatesError: null,
  emrFacilitiesError: null,
};

export default (state = initialState, action) => {
  switch (action.type) {
    /**
     * Nothing in this reducer may survive an account switch.
     *
     * `logout()` dispatches LOGGED_OUT and then `push('/login')` — an in-app route
     * change, so the store is never torn down. This reducer had no reset case at
     * all, so `state.userProfile.email` kept the signed-OUT user's address for the
     * lifetime of the tab. Anything keying off it then resolved to the wrong
     * person after the next sign-in: the tutorial progress store namespaced user
     * B's checkmarks under user A, and `has_completed_tutorial` from A silently
     * suppressed B's onboarding. Shared clinical workstations do exactly this.
     *
     * String literals rather than importing USER_AUTH so this reducer's unit test
     * does not pull actions/auth.js (axios/Sentry/posthog) into scope — the same
     * convention reducers/tutorial.js and records.js use. USER_LOGOUT_FAILED is
     * included because logout()'s catch branch calls resetAuth() without ever
     * dispatching LOGGED_OUT.
     */
    case 'LOGGED_OUT':
    case 'USER_LOGOUT_FAILED':
      return initialState;
    case 'BEGIN_CHANGE_EMAIL_PREFERENCE':
      return {
        ...state,
        notificationErrorMessage: '',
      };
    case 'FAILED_CHANGE_EMAIL_PREFERENCE':
      return {
        ...state,
        notificationErrorMessage: 'An error has occured, please try again later.',
      };
    case 'FINISHED_CHANGE_EMAIL_PREFERENCE':
      return {
        ...state,
        notification_fax_email: action.payload.data.notification_fax_email,
        notificationErrorMessage: '',
      };
    case 'BEGIN_CHANGE_EMAIL_PREFERENCE_FOR_EMAILED_RECORDS':
      return {
        ...state,
        notificationErrorMessage: '',
      };
    case 'FAILED_CHANGE_EMAIL_PREFERENCE_FOR_EMAILED_RECORDS':
      return {
        ...state,
        notificationErrorMessage: 'An error has occured, please try again later.',
      };
    case 'FINISHED_CHANGE_EMAIL_PREFERENCE_FOR_EMAILED_RECORDS':
      return {
        ...state,
        email_notification_emailed_records: action.payload.data.email_notification_emailed_records,
        notificationErrorMessage: '',
      };
    case 'BEGIN_CHANGE_EMAIL_PREFERENCE_FOR_ASSIGNED_RECORDS':
      return {
        ...state,
        notificationErrorMessage: '',
      };
    case 'FAILED_CHANGE_EMAIL_PREFERENCE_FOR_ASSIGNED_RECORDS':
      return {
        ...state,
        notificationErrorMessage: 'An error has occured, please try again later.',
      };
    case 'FINISHED_CHANGE_EMAIL_PREFERENCE_FOR_ASSIGNED_RECORDS':
      return {
        ...state,
        email_notification_assigned_records: action.payload.data.email_notification_assigned_records,
        notificationErrorMessage: '',
      };
    case 'BEGIN_CHANGE_EMR_UPLOAD_FAILURE_PREFERENCE':
      return {
        ...state,
        notificationErrorMessage: '',
      };
    case 'FAILED_CHANGE_EMR_UPLOAD_FAILURE_PREFERENCE':
      return {
        ...state,
        notificationErrorMessage: 'An error has occured, please try again later.',
      };
    case 'FINISHED_CHANGE_EMR_UPLOAD_FAILURE_PREFERENCE':
      return {
        ...state,
        email_notification_emr_upload_failures: action.payload.data.email_notification_emr_upload_failures,
        notificationErrorMessage: '',
      };
    case 'BEGIN_CHANGE_FAILED_FAX_PREFERENCE':
      return {
        ...state,
        notificationErrorMessage: '',
      };
    case 'FAILED_CHANGE_FAILED_FAX_PREFERENCE':
      return {
        ...state,
        notificationErrorMessage: 'An error has occured, please try again later.',
      };
    case 'FINISHED_CHANGE_FAILED_FAX_PREFERENCE':
      return {
        ...state,
        failedSentFaxesNotification: action.payload.data.failed_sent_faxes_notification,
        notificationErrorMessage: '',
      };
    case 'BEGIN_GET_USER_INFO':
      // In flight. Lets an app-wide owner fetch the profile without racing the
      // route-level callers that already do (containers/Home).
      return {
        ...state,
        userInfoLoading: true,
      };
    case 'FAILED_GET_USER_INFO':
      // Settled, just not successfully. Consumers gating on `userInfoLoaded` must
      // not hang forever on a failed fetch — they fall back to their own defaults.
      // Consumers that would BURN something on the value read must additionally
      // check `userInfoLoadFailed`; the defaults are not this user's answers.
      return {
        ...state,
        userInfoLoading: false,
        userInfoLoaded: true,
        userInfoLoadFailed: true,
      };
    case 'FINISHED_GET_USER_INFO': {
      const { data } = action.payload;
      data.emr = data.emr || null;
      data.job_title = data.job_title || '';
      return {
        ...state,
        ...data,
        isAdmin: data.is_admin,
        // The server returns this preference snake_cased, but the rest of the app
        // (and every other reducer case) reads the camelCased key. Without this
        // mapping the spread above leaves `failedSentFaxesNotification` stuck at
        // its initial `false` no matter what the user actually has saved.
        failedSentFaxesNotification: !!data.failed_sent_faxes_notification,
        userInfoLoading: false,
        userInfoLoaded: true,
        userInfoLoadFailed: false,
      };
    }
    case 'BEGIN_GET_USER_DEPARTMENTS':
      return {
        ...state,
      };
    case 'FAILED_GET_USER_DEPARTMENTS':
      return {
        ...state,
      };
    case 'FINISHED_GET_USER_DEPARTMENTS': {
      return {
        ...state,
        isLoading: false,
        departmentUsers: action.payload.data.users,
        headers: action.payload.data.meta,
      };
    }
    case 'BEGIN_LOGIN_ADMIN':
      return {
        ...state,
        loginErrorMessage: '',
        loginSuccess: false,
      };
    case 'FAILED_LOGIN_ADMIN': {
      const forceRefresh = action.payload.response.data.force || false;
      let message = 'You do not have admin privileges. Please contact your admin if you would like admin privileges.';
      if (forceRefresh) {
        setTimeout(() => {
          window.location.reload();
        }, 200);
        message = 'You have logged out of the admin dashboard. Please refresh the page and sign in.';
      }

      return {
        ...state,
        loginSuccess: false,
        loginErrorMessage: message,
      };
    }
    case 'FINISHED_LOGIN_ADMIN':
      return {
        ...state,
        loginSuccess: true,
        loginErrorMessage: '',
      };
    case 'BEGIN_UPDATE_USER_DETAILS': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: false,
      };
    }
    case 'FINISHED_UPDATE_USER_DETAILS': {
      const { data } = action.payload;
      localStorage.setItem('m8_emr', data.emr);
      localStorage.setItem('m8_nymbl_tenant_id', getTenantId(data.available_emrs));
      localStorage.setItem('m8_job_title', data.job_title);
      return {
        ...state,
        isLoading: false,
        job_title: data.job_title,
        notification_fax_email: data.notification_fax_email,
        email_notification_emailed_records: data.email_notification_emailed_records,
        email_notification_assigned_records: data.email_notification_assigned_records,
        email_notification_emr_upload_failures: data.email_notification_emr_upload_failures,
        failedSentFaxesNotification: data.failed_sent_faxes_notification,
        is_clinical_provider: data.is_clinical_provider,
        npi: data.npi,
        walkthrough_start_destination: data.walkthrough_start_destination,
        // Narrowly mirror the completion flag back from the response
        // (`current_user.object` carries it). Without this,
        // `persistTutorialCompletion`'s `if (hasCompletedTutorial) return`
        // short-circuit could never latch, so every later completion and dismiss
        // re-sent the same PATCH for the rest of the session. Deliberately one
        // field, not a spread of the whole body — this response has a different
        // shape from GET's and spreading it would clobber unrelated profile state.
        has_completed_tutorial: action.payload.data.has_completed_tutorial === true
          || state.has_completed_tutorial === true,
      };
    }
    case 'FAILED_UPDATE_USER_DETAILS': {
      const attemptedUsersUpdate = parseFailedUserDetailsPayload(action.payload)?.users || {};

      if (Object.prototype.hasOwnProperty.call(attemptedUsersUpdate, 'emr')) {
        localStorage.setItem('m8_emr', state.emr || '');

        const confirmedTenantId = getTenantId(state.available_emrs);
        if (confirmedTenantId) {
          localStorage.setItem('m8_nymbl_tenant_id', confirmedTenantId);
        } else {
          localStorage.removeItem('m8_nymbl_tenant_id');
        }
      }

      if (Object.prototype.hasOwnProperty.call(attemptedUsersUpdate, 'job_title')) {
        localStorage.setItem('m8_job_title', state.job_title || '');
      }

      return {
        ...state,
        isLoading: false,
        errorCode: action.payload.response.status,
        errorMessage: handlingErrors(action.payload.response.status, 'Unable to update your information. Please try again.'),
        error: true,
      };
    }
    case 'BEGIN_SEND_OTP_TO_SMS': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: false,
      };
    }
    case 'FAILED_SEND_OTP_TO_SMS': {
      return {
        ...state,
        isLoading: false,
        error: true,
        errorMessage: 'error',
      };
    }
    case 'FINISHED_SEND_OTP_TO_SMS': {
      return {
        ...state,
        isLoading: false,
        error: false,
        errorMessage: false,
      };
    }
    case 'BEGIN_VERIFY_SMS_OTP': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: false,
      };
    }
    case 'FAILED_VERIFY_SMS_OTP': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: false,
      };
    }
    case 'FINISHED_VERIFY_SMS_OTP': {
      return {
        ...state,
        isLoading: true,
        error: action.payload.data.message !== 'success',
        errorMessage: action.payload.data.message !== 'success' ? action.payload.data.message : '',
      };
    }
    case 'BEGIN_GET_ALL_DEPARTMENTS': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: false,
      };
    }
    case 'FINISHED_GET_ALL_DEPARTMENTS': {
      return {
        ...state,
        isLoading: false,
        allOrganizationDepartments: action.payload.data.departments,
      };
    }
    case 'FAILED_GET_ALL_DEPARTMENTS': {
      return {
        ...state,
        isLoading: false,
        error: true,
        errorMessage: action.payload.response,
      };
    }
    case 'BEGIN_GET_DOCUMENT_CATEGORIES': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: false,
      };
    }
    case 'FINISHED_GET_DOCUMENT_CATEGORIES': {
      return {
        ...state,
        isLoading: false,
        allDocumentCategories: action.payload.data,
      };
    }
    case 'BEGIN_GET_TASK_TEMPLATE_CATEGORY_USAGE': {
      return {
        ...state,
        error: false,
      };
    }
    case 'FINISHED_GET_TASK_TEMPLATE_CATEGORY_USAGE': {
      return {
        ...state,
        taskTemplateCategoryUsage: action.payload.data.category_usage,
      };
    }
    case 'FAILED_GET_TASK_TEMPLATE_CATEGORY_USAGE': {
      return {
        ...state,
        error: true,
        errorMessage: action.payload.response,
      };
    }
    case 'BEGIN_GET_MARKETING_REPRESENTATIVES': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: false,
      };
    }
    case 'FINISHED_GET_MARKETING_REPRESENTATIVES': {
      return {
        ...state,
        isLoading: false,
        allMarketingRepresentatives: action.payload.data.marketing_representatives,
      };
    }
    case 'FAILED_GET_MARKETING_REPRESENTATIVES': {
      return {
        ...state,
        error: true,
        errorMessage: action.payload.response,
      };
    }
    case 'FAILED_EMA_DOCUMENT_CATEGORIES': {
      return {
        ...state,
        error: true,
        errorMessage: action.payload.response,
      };
    }
    // Both EMA and Tebra patient searches update the same generic state
    case 'BEGIN_GET_EMA_PATIENTS':
    case 'BEGIN_GET_TEBRA_PATIENTS': {
      return {
        ...state,
        isLoading: true,
        apiError: false,
        apiErrorMessage: '',
      };
    }
    case 'FINISHED_GET_EMA_PATIENTS':
    case 'FINISHED_GET_TEBRA_PATIENTS': {
      return {
        ...state,
        isLoading: false,
        apiPatients: action.payload.data,
      };
    }
    case 'FAILED_GET_EMA_PATIENTS':
    case 'FAILED_GET_TEBRA_PATIENTS': {
      return {
        ...state,
        apiError: true,
        apiErrorMessage: action.payload.response.data.error,
      };
    }
    case 'BEGIN_GET_REFERRED_STATUSES': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: false,
      };
    }
    case 'FINISHED_GET_REFERRED_STATUSES': {
      return {
        ...state,
        isLoading: false,
        allReferredStatuses: action.payload.data.referral_statuses,
        referralStatusBarShowUnpinned: action.payload.data.show_unpinned_statuses !== false,
      };
    }
    case 'FAILED_GET_REFERRED_STATUSES': {
      return {
        ...state,
        error: true,
        errorMessage: action.payload.response,
      };
    }
    case 'BEGIN_UPDATE_REFERRAL_STATUS_BAR_PREFERENCES': {
      return {
        ...state,
        referralStatusBarPreferencesSaving: true,
        error: false,
        errorMessage: false,
      };
    }
    case 'FINISHED_UPDATE_REFERRAL_STATUS_BAR_PREFERENCES': {
      return {
        ...state,
        referralStatusBarPreferencesSaving: false,
        allReferredStatuses: action.payload.data.referral_statuses,
        referralStatusBarShowUnpinned: action.payload.data.show_unpinned_statuses !== false,
      };
    }
    case 'FAILED_UPDATE_REFERRAL_STATUS_BAR_PREFERENCES': {
      return {
        ...state,
        referralStatusBarPreferencesSaving: false,
        error: true,
        errorMessage: action.payload.response,
      };
    }
    case 'BEGIN_GET_DOCUMENT_STATUSES': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: false,
      };
    }
    case 'FINISHED_GET_DOCUMENT_STATUSES': {
      return {
        ...state,
        isLoading: false,
        allDocumentStatuses: action.payload.data,
        headers: action.payload.data.meta,
      };
    }
    case 'FAILED_GET_DOCUMENT_STATUSES': {
      return {
        ...state,
        error: true,
        errorMessage: action.payload.response,
      };
    }
    case 'BEGIN_GET_SUBSCRIPTIONS': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: false,
      };
    }
    case 'FINISHED_GET_SUBSCRIPTIONS': {
      return {
        ...state,
        isLoading: false,
        allActiveFeatures: action.payload.data.features,
        billingV2Routed: action.payload.data.billing_v2_routed === true,
        headers: action.payload.data.meta,
      };
    }
    case 'FINISHED_CREATE_SUBSCRIPTION':
    case 'FINISHED_DELETE_SUBSCRIPTION': {
      return {
        ...state,
        allActiveFeatures: action.payload.data.features,
      };
    }
    case 'FAILED_GET_SUBSCRIPTIONS': {
      return {
        ...state,
        error: true,
        errorMessage: action.payload.response,
      };
    }
    case 'BEGIN_GET_EMA_TASK_TEMPLATES': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: false,
      };
    }
    case 'FINISHED_GET_EMA_TASK_TEMPLATES': {
      return {
        ...state,
        isLoading: false,
        allEmaTaskTemplates: action.payload.data,
        headers: action.payload.data.meta,
      };
    }
    case 'FAILED_GET_EMA_TASK_TEMPLATES': {
      return {
        ...state,
        error: true,
        errorMessage: action.payload.response,
      };
    }
    case 'BEGIN_GET_EMA_TASK_PROVIDERS': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: false,
      };
    }
    case 'FINISHED_GET_EMA_TASK_PROVIDERS': {
      return {
        ...state,
        isLoading: false,
        allEmaTaskProviders: action.payload.data,
        headers: action.payload.data.meta,
      };
    }
    case 'FAILED_GET_EMA_TASK_PROVIDERS': {
      return {
        ...state,
        error: true,
        errorMessage: action.payload.response,
      };
    }
    case 'BEGIN_GET_KAREO_TASK_PROVIDERS': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: false,
      };
    }
    case 'FINISHED_GET_KAREO_TASK_PROVIDERS': {
      return {
        ...state,
        isLoading: false,
        allKareoTaskProviders: action.payload.data,
      };
    }
    case 'FAILED_GET_KAREO_TASK_PROVIDERS': {
      return {
        ...state,
        error: true,
        errorMessage: action.payload.response,
      };
    }
    case 'BEGIN_GET_TASK_PROVIDERS': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: false,
      };
    }
    case 'FINISHED_GET_TASK_PROVIDERS': {
      return {
        ...state,
        isLoading: false,
        allEmaTaskProviders: action.payload.data,
        headers: action.payload.data.meta,
      };
    }
    case 'FAILED_UPDATE_EMA_TASK_PROVIDER': {
      return {
        ...state,
        error: true,
        errorCode: action.payload.response.status,
      };
    }
    case 'FAILED_DELETE_EMA_TASK_PROVIDER': {
      return {
        ...state,
        error: true,
        errorCode: action.payload.response.status,
      };
    }
    case 'FAILED_CREATE_EMA_TASK_PROVIDER': {
      return {
        ...state,
        error: true,
        errorCode: action.payload.response.status,
      };
    }
    case 'FAILED_GET_TASK_PROVIDERS': {
      return {
        ...state,
        error: true,
        errorMessage: action.payload.response,
      };
    }
    case 'CHANGE_BILLING_MODAL_STATE':
      return {
        ...state,
        modalState: action.state,
        modalOpen: action.open,
      };
    case 'BEGIN_GET_PAYER_NAMES': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: false,
      };
    }
    case 'FINISHED_GET_PAYER_NAMES': {
      const responseData = action.payload.data;
      const allPayerNames = responseData.map(item => item.payer_name);
      const payerPlanNamesMap = responseData.reduce((acc, item) => {
        acc[item.payer_name] = item.plan_names || [];
        return acc;
      }, {});
      return {
        ...state,
        isLoading: false,
        allPayerNames,
        payerPlanNamesMap,
      };
    }
    case 'FAILED_GET_PAYER_NAMES': {
      return {
        ...state,
        error: true,
        errorMessage: action.payload.response,
      };
    }
    case 'BEGIN_GET_DEFAULT_SMS_TEMPLATE': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: false,
        defaultSmsTemplate: null,
      };
    }
    case 'FINISHED_GET_DEFAULT_SMS_TEMPLATE': {      
      return {
        ...state,
        isLoading: false,
        defaultSmsTemplate: action.payload.data,
      };
    }
    case 'FAILED_GET_DEFAULT_SMS_TEMPLATE': {
      return {
        ...state,
        isLoading: false,
        error: true,
        errorMessage: action.payload.response,
        defaultSmsTemplate: null,
      };
    }
    case 'BEGIN_GET_DEFAULT_OUTBOUND_CALL_TEMPLATE': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: false,
      };
    }
    case 'FINISHED_GET_DEFAULT_OUTBOUND_CALL_TEMPLATE': {
      return {
        ...state,
        isLoading: false,
        defaultOutboundCallTemplate: action.payload.data,
      };
    }
    case 'FAILED_GET_DEFAULT_OUTBOUND_CALL_TEMPLATE': {
      return {
        ...state,
        isLoading: false,
        error: true,
        errorMessage: action.payload.response,
        defaultOutboundCallTemplate: null,
      };
    }
    case 'FINISHED_CHECKING_MULTI_ORG_LOGIN':
      return {
        ...state,
        email_notification_emailed_records: action.payload.data.user.email_notification_emailed_records,
        email_notification_assigned_records: action.payload.data.user.email_notification_assigned_records,
        email_notification_emr_upload_failures: action.payload.data.user.email_notification_emr_upload_failures,
        notification_fax_email: action.payload.data.user.notification_fax_email,
        failedSentFaxesNotification: action.payload.data.user.failed_sent_faxes_notification,
      };
    case 'BEGIN_GET_DISTRIBUTION_LISTS': {
      return {
        ...state,
        isLoading: true,
        error: false,
        errorMessage: false,
      };
    }
    case 'FAILED_GET_DISTRIBUTION_LISTS': {
      return {
        ...state,
        error: true,
        errorMessage: action.payload.response,
      };
    }
    case 'FINISHED_GET_DISTRIBUTION_LISTS': {
      return {
        ...state,
        isLoading: false,
        distributionLists: action.payload.data,
      };
    }
    case 'FINISHED_GET_AUTO_ARCHIVE_SETTINGS': {
      return {
        ...state,
        isLoading: false,
        autoArchiveSettings: action.payload.data.settings,
      };
    }
    case 'FINISHED_UPDATE_AUTO_ARCHIVE_SETTINGS': {
      return {
        ...state,
        isLoading: false,
        autoArchiveSettings: action.payload.data.settings,
      };
    }
    case 'FINISHED_GET_AUTO_ARCHIVE_LABELS': {
      return {
        ...state,
        isLoading: false,
        autoArchiveLabels: action.payload.data.labels || [],
      };
    }
    case 'FINISHED_GET_AUTO_ARCHIVE_ON_LABEL_SETTINGS':
    case 'FINISHED_UPDATE_AUTO_ARCHIVE_ON_LABEL_SETTINGS': {
      return {
        ...state,
        isLoading: false,
        autoArchiveOnLabelSettings: action.payload.data.settings,
      };
    }
    case 'FINISHED_GET_EMR_AUTO_UPLOAD_CATEGORY_SETTINGS': {
      return {
        ...state,
        isLoading: false,
        emrAutoUploadCategorySettings: action.payload.data.settings || {},
      };
    }
    case 'FINISHED_UPDATE_EMR_AUTO_UPLOAD_CATEGORY_SETTINGS': {
      return {
        ...state,
        isLoading: false,
        emrAutoUploadCategorySettings: action.payload.data.settings || {},
      };
    }
    case 'FINISHED_GET_EMA_RESULTS_PROVIDER_SETTINGS':
    case 'FINISHED_UPDATE_EMA_RESULTS_PROVIDER_SETTINGS': {
      return {
        ...state,
        isLoading: false,
        emaResultsProviderSettings: action.payload.data,
      };
    }
    case 'FINISHED_GET_MODMED_QUEUED_FAX_SETTINGS': {
      return {
        ...state,
        isLoading: false,
        modmedQueuedFaxSettings: {
          ...action.payload.data.settings,
          has_ema_credentials: action.payload.data.has_ema_credentials,
        },
      };
    }
    case 'FINISHED_UPDATE_MODMED_QUEUED_FAX_SETTINGS': {
      return {
        ...state,
        isLoading: false,
        modmedQueuedFaxSettings: {
          ...action.payload.data.settings,
          has_ema_credentials: action.payload.data.has_ema_credentials,
        },
      };
    }
    case 'FINISHED_GET_ARM_KILL_SWITCH_SETTINGS': {
      return {
        ...state,
        isLoading: false,
        armKillSwitchSettings: action.payload.data.settings,
      };
    }
    case 'FINISHED_UPDATE_ARM_KILL_SWITCH_SETTINGS': {
      return {
        ...state,
        isLoading: false,
        armKillSwitchSettings: action.payload.data.settings,
      };
    }
    case 'FINISHED_GET_EMR_TEMPLATES': {
      return {
        ...state,
        isLoading: false,
        emrTemplates: action.payload.data.templates,
        emrTemplatesLoaded: true,
        emrTemplatesError: action.payload.data.error || null,
      };
    }
    case 'FAILED_GET_EMR_TEMPLATES': {
      return {
        ...state,
        isLoading: false,
        emrTemplatesLoaded: true,
        emrTemplatesError: action.payload?.response?.data?.error || 'Failed to load templates',
      };
    }
    case 'FINISHED_GET_EMR_FACILITIES': {
      return {
        ...state,
        isLoading: false,
        emrFacilities: action.payload.data.facilities,
        emrFacilitiesLoaded: true,
        emrFacilitiesError: action.payload.data.error || null,
      };
    }
    case 'FAILED_GET_EMR_FACILITIES': {
      return {
        ...state,
        isLoading: false,
        emrFacilitiesLoaded: true,
        emrFacilitiesError: action.payload?.response?.data?.error || 'Failed to load facilities',
      };
    }
    default:
      return state;
  }
};
